// Simple theme hook to manage dark/light mode with Tailwind 'class' strategy
import { useState, useEffect, useCallback } from 'react';

const THEME_KEY = 'theme'; // localStorage key

function getInitialTheme() {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (_) {}
  // Product chrome is dark-first (auth/landing/portal). Default dark unless user chose light.
  return 'dark';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement; // Tailwind expects .dark on <html>
  if (theme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
}

export function useTheme() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(THEME_KEY, theme); } catch (_) {}
  }, [theme]);

  useEffect(() => {
    // Only auto-sync if user hasn't explicitly chosen a theme yet
    try {
      if (window.localStorage.getItem(THEME_KEY)) return;
    } catch (_) {}
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(t => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, setTheme, toggleTheme };
}

export default useTheme;
